import { useEffect, useState } from "react";
import Skeleton from "../../../reusable/Skeleton/Skeleton";
import StatusChip from "../../../reusable/StatusChip/StatusChip";
import { toast } from "../../../reusable/Toast/toastStore";
import { authErrorMessage } from "../../../store/api/authHelpers";
import { useGetVeriffStatusQuery, useLazyGetVeriffLinkQuery } from "../../../store/api/veriffApi";
import {
  useGetIsNigeriaQuery,
  useGetKycaidDecisionQuery,
  useLazyGetKycaidFormUrlQuery,
} from "../../../store/api/kycaidApi";
import { KYC_TITLE } from "../kycStatus";
import { t } from "../../../i18n/i18n";
import "./KycVerification.scss";

/** Chip and status-line tone per status — the chip palette, nothing new. */
const STATUS_TONE = {
  pending: "warning",
  under_review: "info",
  approved: "success",
  rejected: "danger",
};

const PROVIDERS = {
  veriff: {
    name: "Veriff",
    label: "Identity check · powered by Veriff",
    logo: "/icons/kyc-veriff.svg",
    loadingText: "Opening Veriff…",
    retryText: "Try again with Veriff",
    texts: {
      pending: "Scan your ID and take a quick selfie with Veriff — about 2 minutes.",
      under_review: "Veriff is checking your session. This usually takes a few minutes.",
      rejected: "Veriff could not confirm your identity. Start a new session to try again.",
    },
    howItWorks: [
      {
        title: "Start a Veriff session",
        desc: "Takes you to Veriff's secure page. You can switch to your phone with the QR code it shows.",
      },
      {
        title: "Scan your ID and take a selfie",
        desc: "Veriff matches your face to the photo on your ID in real time.",
      },
      {
        title: "Get your result in minutes",
        desc: "The status here updates on its own once Veriff has decided.",
      },
    ],
    note: "Veriff only uses your images to confirm your identity for Alpha Capital.",
  },
  kycaid: {
    name: "KYCAID",
    label: "Identity check · powered by KYCAID",
    logo: "/icons/kyc-kycaid.svg",
    loadingText: "Opening KYCAID…",
    retryText: "Try again with KYCAID",
    texts: {
      pending: "Verify your identity with KYCAID — about 2 minutes.",
      under_review: "KYCAID is reviewing your verification. This usually takes a few minutes.",
      rejected: "KYCAID could not confirm your identity. Start a new session to try again.",
    },
    howItWorks: [
      {
        title: "Start a KYCAID session",
        desc: "Takes you to KYCAID's secure verification form.",
      },
      {
        title: "Scan your ID and take a selfie",
        desc: "KYCAID matches your face to the photo on your ID in real time.",
      },
      {
        title: "Get your result in minutes",
        desc: "The status here updates on its own once KYCAID has decided.",
      },
    ],
    note: "KYCAID only uses your images to confirm your identity for Alpha Capital.",
  },
};

const NEEDS = [
  "A valid government-issued photo ID — passport, national ID or driving licence",
  "A phone or computer with a working camera",
  "Good lighting and about 2 minutes",
];

/** Leading glyph for the status line — a shield tick once cleared, a clock otherwise. */
function StatusGlyph({ approved }) {
  return approved ? (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M8 1.8 2.9 3.8v3.6c0 3 2 5.5 5.1 6.4 3.1-.9 5.1-3.4 5.1-6.4V3.8L8 1.8Z" />
      <path d="m5.8 8 1.5 1.5 3-3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <circle cx="8" cy="8" r="5.6" />
      <path d="M8 5v3.2l2 1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * KYC Verification — identity check: a live ID scan and selfie.
 * Nigerian traders use KYCAID; all other traders use Veriff.
 */
export default function KycVerification() {
  const { data: isNigeria, isLoading: isNigeriaLoading } = useGetIsNigeriaQuery();
  const isNigerian = Boolean(isNigeria);

  const { data: veriff, isLoading: veriffLoading, refetch: refetchVeriff } = useGetVeriffStatusQuery(undefined, {
    skip: isNigerian,
  });
  const [getVeriffLink] = useLazyGetVeriffLinkQuery();

  const { data: kycaidDecision, isLoading: kycaidDecisionLoading, refetch: refetchKycaidDecision } = useGetKycaidDecisionQuery(undefined, {
    skip: !isNigerian,
  });
  const [getKycaidFormUrl] = useLazyGetKycaidFormUrlQuery();

  // Stays set once the redirect starts; resets when trader returns to this tab/window.
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    const handleReturn = () => {
      setStarting(false);
      if (isNigerian) {
        refetchKycaidDecision?.();
      } else {
        refetchVeriff?.();
      }
    };

    window.addEventListener("pageshow", handleReturn);
    window.addEventListener("focus", handleReturn);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleReturn();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("pageshow", handleReturn);
      window.removeEventListener("focus", handleReturn);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isNigerian, refetchKycaidDecision, refetchVeriff]);

  const provider = isNigerian ? PROVIDERS.kycaid : PROVIDERS.veriff;
  const isLoading = isNigeriaLoading || (isNigerian ? kycaidDecisionLoading : veriffLoading);

  let status = "pending";
  if (isNigerian) {
    const decisionData = kycaidDecision?.data ?? kycaidDecision;
    const adminStatus = (decisionData?.admin_status || "").trim().toLowerCase();
    const reviewStatus = (decisionData?.review_status || "").trim().toLowerCase();
    const isVerified = Boolean(decisionData?.is_verified || veriff?.is_verified);

    // 1. Approved: admin approved / manual approved or is_verified is true
    if (
      isVerified ||
      adminStatus === "manual approved" ||
      adminStatus === "approved"
    ) {
      status = "approved";
    }
    // 2. Rejected: review failed or admin rejected
    else if (
      reviewStatus === "rejected" ||
      reviewStatus === "failed" ||
      adminStatus === "rejected" ||
      adminStatus === "manual rejected"
    ) {
      status = "rejected";
    }
    // 3. Submitted & Under Review: trader submitted KYC (review_status: completed or pending),
    // awaiting admin approval (admin_status: pending, is_verified: false)
    else if (
      (reviewStatus === "completed" || reviewStatus === "pending") &&
      adminStatus === "pending"
    ) {
      status = "under_review";
    }
    // 4. Not Submitted / Incomplete: review_status is notAvailable, trader did not submit yet
    // Trader can initiate / re-initiate KYC
    else {
      status = "pending";
    }
  } else {
    status = veriff?.is_verified ? "approved" : "pending";
  }

  const approved = status === "approved";
  const canStart = !isLoading && !approved && status !== "under_review";

  const start = async () => {
    if (starting) return;
    setStarting(true);
    try {
      if (isNigerian) {
        const data = await getKycaidFormUrl().unwrap();
        const url = data?.form_url;
        if (!url) {
          const msg = data?.detail || data?.message;
          if (msg) toast.info(t(msg));
          else throw new Error("No KYCAID link in the response");
          setStarting(false);
          return;
        }
        window.location.assign(url);
      } else {
        const data = await getVeriffLink({ retry: status === "rejected" }).unwrap();
        const url = data?.verification?.url;
        if (!url) {
          const msg = data?.detail || data?.message;
          if (msg) toast.info(t(msg));
          else throw new Error("No Veriff link in the response");
          setStarting(false);
          return;
        }
        window.location.assign(url);
      }
    } catch (err) {
      setStarting(false);
      const apiMsg = err?.data?.detail || err?.data?.message || (err?.data ? authErrorMessage(err) : null);
      toast.error(
        t(apiMsg) || t("Could not start {{provider}}. Please try again in a moment.", { provider: provider.name })
      );
    }
  };

  const text = approved
    ? t("Your KYC has been completed.")
    : t(provider.texts[status] ?? provider.texts.pending);

  return (
    <div className="kyc">
      <section className="kyc-provider" aria-label={t("{{provider}} identity check", { provider: provider.name })}>
        <div className="kyc-provider__brand">
          <img className="kyc-provider__logo" src={provider.logo} alt={provider.name} draggable="false" />
        </div>

        <div className="kyc-provider__body">
          <span className="kyc-provider__label">{t(provider.label)}</span>

          <div className="kyc-provider__title-row">
            <h2 className="kyc-provider__title">{t("KYC Verification")}</h2>
            {isLoading ? (
              <Skeleton w="76px" h={22} pill />
            ) : (
              <StatusChip tone={STATUS_TONE[status]} size="sm">
                {approved ? t("Completed") : t(KYC_TITLE[status] ?? "Not started")}
              </StatusChip>
            )}
          </div>

          {isLoading ? (
            <>
              <Skeleton w="min(300px, 100%)" h={13} />
              <Skeleton w="min(220px, 100%)" h={11} />
            </>
          ) : (
            <>
              <p className={`kyc-provider__status is-${STATUS_TONE[status]}`}>
                <StatusGlyph approved={approved} />
                {text}
              </p>

              <p className="kyc-provider__next">
                {approved
                  ? t("One check covers every account you hold. You will not need to repeat it.")
                  : t("A live ID scan and selfie confirm it is really you.")}
              </p>
            </>
          )}
        </div>

        {canStart && (
          <button type="button" className="kyc-provider__button" onClick={start} disabled={starting}>
            {starting
              ? t(provider.loadingText)
              : status === "rejected"
                ? t(provider.retryText)
                : t("Start verification")}
          </button>
        )}
      </section>

      <section className="kyc-columns">
        <div className="kyc-card">
          <div className="kyc-card__head">{t("How {{provider}} works", { provider: provider.name })}</div>
          <ol className="kyc-steps">
            {provider.howItWorks.map((step, i) => (
              <li key={step.title} className="kyc-steps__item">
                <span className="kyc-steps__number">{i + 1}</span>
                <div>
                  <p className="kyc-steps__title">{t(step.title)}</p>
                  <p className="kyc-steps__desc">{t(step.desc)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="kyc-card">
          <div className="kyc-card__head">{t("What you will need")}</div>
          <ul className="kyc-needs">
            {NEEDS.map((need) => (
              <li key={need}>{t(need)}</li>
            ))}
          </ul>
          <p className="kyc-card__note">
            {t(provider.note)}
          </p>
        </div>
      </section>
    </div>
  );
}
